/**
 * KeratometrySection Component
 *
 * Displays keratometry data in a tabular format showing OD (right eye) and OS (left eye)
 * measurements including flat K, steep K, average K, and axis values. Also displays
 * method and mire quality information. Provides an editable interface for updating
 * keratometry measurements.
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface KeratometryData {
  // OD (Right Eye) fields
  keratometryFlatKOD?: string;
  keratometryFlatAxisOD?: string;
  keratometrySteepKOD?: string;
  keratometrySteepAxisOD?: string;
  keratometryAvgKOD?: string;

  // OS (Left Eye) fields
  keratometryFlatKOS?: string;
  keratometryFlatAxisOS?: string;
  keratometrySteepKOS?: string;
  keratometrySteepAxisOS?: string;
  keratometryAvgKOS?: string;

  // General fields
  keratometryMethod?: string;
  keratometryMireQuality?: string;

  [key: string]: any; // Allow for other properties in the JSON
}

interface KeratometryApiResponse {
  testKey: string;
  data: KeratometryData[];
}

// Legacy data structure interface (for backward compatibility)
interface KeratometryLegacyData {
  keratometryFlatKOD?: string;
  keratometryFlatAxisOD?: string;
  keratometrySteepKOD?: string;
  keratometrySteepAxisOD?: string;
  keratometryAvgKOD?: string;
  keratometryFlatKOS?: string;
  keratometryFlatAxisOS?: string;
  keratometrySteepKOS?: string;
  keratometrySteepAxisOS?: string;
  keratometryAvgKOS?: string;
  keratometryMethod?: string;
  keratometryMireQuality?: string;
  [key: string]: any; // Allow for other properties in the JSON
}

// Nested structure interface (for pages[0].data format)
interface KeratometryNestedData {
  pages?: Array<{
    data?: KeratometryData;
    [key: string]: any;
  }>;
  [key: string]: any;
}

// Union type to handle both new and legacy formats, including array format
type KeratometryDataUnion =
  | KeratometryApiResponse
  | KeratometryLegacyData
  | KeratometryApiResponse[]
  | KeratometryNestedData
  | null;

interface KeratometrySectionProps {
  jsonContent: KeratometryDataUnion;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: KeratometryDataUnion) => Promise<void>;
  onAccept?: () => void;
}

const KeratometrySection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: KeratometrySectionProps) => {
  const [currentData, setCurrentData] = useState<KeratometryData | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<KeratometryData>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper function to extract the actual keratometry data from either format
  const extractKeratometryData = useCallback(
    (data: KeratometryDataUnion): KeratometryData | null => {
      if (!data) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          return firstItem.data[0] || null;
        }
      }

      // Check if it's the new API response format (single object)
      if ('testKey' in data && 'data' in data && Array.isArray(data.data)) {
        return data.data[0] || null;
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in data && data.pages?.[0]?.data) {
        return data.pages[0].data;
      }

      // Legacy format: direct object with keratometry fields
      if (
        'keratometryFlatKOD' in data ||
        'keratometryFlatKOS' in data ||
        'keratometryMethod' in data
      ) {
        return data as KeratometryData;
      }

      return null;
    },
    []
  );

  // Helper function to create the appropriate data structure for updates
  const createUpdateData = useCallback(
    (updatedFields: KeratometryData): KeratometryDataUnion => {
      if (!jsonContent) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const updatedDataArray = [
            {
              ...firstItem.data[0],
              ...updatedFields,
            },
          ];

          return [
            {
              ...firstItem,
              data: updatedDataArray,
            },
          ];
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const updatedDataArray = [
          {
            ...jsonContent.data[0],
            ...updatedFields,
          },
        ];

        return {
          ...jsonContent,
          data: updatedDataArray,
        };
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in jsonContent && jsonContent.pages?.[0]?.data) {
        return {
          ...jsonContent,
          pages: [
            {
              ...jsonContent.pages[0],
              data: {
                ...jsonContent.pages[0].data,
                ...updatedFields,
              },
            },
          ],
        };
      }

      // Legacy format: return direct object
      return {
        ...jsonContent,
        ...updatedFields,
      };
    },
    [jsonContent]
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const extractedData = extractKeratometryData(jsonContent);
    setCurrentData(extractedData);
  }, [jsonContent, extractKeratometryData]);

  // Handle updates to the keratometry data
  const handleUpdate = useCallback(
    async (updatedFields: KeratometryData) => {
      if (!currentData) return;

      const updatedData = createUpdateData(updatedFields);

      // Update local state optimistically
      setCurrentData({
        ...currentData,
        ...updatedFields,
      });

      try {
        await onUpdate(updatedData);
      } catch (error) {
        // Revert local state on error
        const extractedData = extractKeratometryData(jsonContent);
        setCurrentData(extractedData);
        throw error;
      }
    },
    [
      currentData,
      jsonContent,
      onUpdate,
      createUpdateData,
      extractKeratometryData,
    ]
  );

  const handleEditClick = () => {
    // Initialize edit values with current data
    setEditValues(currentData || {});
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsUpdating(true);
    try {
      await handleUpdate(editValues);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update keratometry:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditValues({});
  };

  const handleInputChange = (field: string, value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Define table headers
  const headers = ['', 'Flat K', 'Flat Axis', 'Steep K', 'Steep Axis', 'Avg K'];

  // Helper function to get display value with fallback
  const getDisplayValue = (value: string | undefined) => value || '—';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        {/* Main card */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          {/* Inner highlight */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                  Keratometry
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
              {currentData && !error && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                    disabled={isLoading}>
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {onAccept && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAccept();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="space-y-4">
              {error ? (
                <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                  {error === 'Data not found' ? (
                    'No keratometry data available yet'
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Error:</span> {error}
                    </div>
                  )}
                </div>
              ) : isLoading ? (
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Fetching keratometry data...</span>
                </div>
              ) : currentData ? (
                <>
                  {/* General Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="space-y-1">
                      <Label className="text-md font-medium text-slate-600 dark:text-slate-400">
                        Method
                      </Label>
                      <div className="text-md text-slate-700 dark:text-slate-300 bg-slate-200/20 dark:bg-slate-800/50 px-3 py-2 rounded-lg">
                        {getDisplayValue(currentData.keratometryMethod)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-md font-medium text-slate-600 dark:text-slate-400">
                        Mire Quality
                      </Label>
                      <div className="text-md text-slate-700 dark:text-slate-300 bg-slate-200/20 dark:bg-slate-800/50 px-3 py-2 rounded-lg">
                        {getDisplayValue(currentData.keratometryMireQuality)}
                      </div>
                    </div>
                  </div>

                  {/* Keratometry Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {headers.map((header, idx) => (
                            <th
                              key={idx}
                              className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-800/50 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* OD (Right Eye) Row */}
                        <tr className="bg-slate-50/30 dark:bg-slate-800/30">
                          <td className="border bg-slate-200/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                            OD
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryFlatKOD)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryFlatAxisOD)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometrySteepKOD)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(
                              currentData.keratometrySteepAxisOD
                            )}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryAvgKOD)}
                          </td>
                        </tr>
                        {/* OS (Left Eye) Row */}
                        <tr>
                          <td className="border bg-slate-200/50 dark:bg-slate-800/50 border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                            OS
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryFlatKOS)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryFlatAxisOS)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometrySteepKOS)}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(
                              currentData.keratometrySteepAxisOS
                            )}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData.keratometryAvgKOS)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                  No keratometry data available yet
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 text-xl font-semibold">
              Edit Keratometry
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* General Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryMethod"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Method
                  </Label>
                  <Input
                    id="keratometryMethod"
                    value={editValues.keratometryMethod || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryMethod', e.target.value)
                    }
                    placeholder="e.g., Manual, Automated"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryMireQuality"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Mire Quality
                  </Label>
                  <Input
                    id="keratometryMireQuality"
                    value={editValues.keratometryMireQuality || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'keratometryMireQuality',
                        e.target.value
                      )
                    }
                    placeholder="e.g., Good, Fair, Poor"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
              </div>
            </div>

            {/* OD (Right Eye) Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 border-b border-gray-400/40 dark:border-slate-700 pb-2">
                OD (Right Eye)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryFlatKOD"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Flat K
                  </Label>
                  <Input
                    id="keratometryFlatKOD"
                    value={editValues.keratometryFlatKOD || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryFlatKOD', e.target.value)
                    }
                    placeholder="e.g., 42.50"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryFlatAxisOD"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Flat Axis
                  </Label>
                  <Input
                    id="keratometryFlatAxisOD"
                    value={editValues.keratometryFlatAxisOD || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryFlatAxisOD', e.target.value)
                    }
                    placeholder="e.g., 180"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometrySteepKOD"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Steep K
                  </Label>
                  <Input
                    id="keratometrySteepKOD"
                    value={editValues.keratometrySteepKOD || ''}
                    onChange={(e) =>
                      handleInputChange('keratometrySteepKOD', e.target.value)
                    }
                    placeholder="e.g., 43.50"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometrySteepAxisOD"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Steep Axis
                  </Label>
                  <Input
                    id="keratometrySteepAxisOD"
                    value={editValues.keratometrySteepAxisOD || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'keratometrySteepAxisOD',
                        e.target.value
                      )
                    }
                    placeholder="e.g., 90"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryAvgKOD"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Avg K
                  </Label>
                  <Input
                    id="keratometryAvgKOD"
                    value={editValues.keratometryAvgKOD || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryAvgKOD', e.target.value)
                    }
                    placeholder="e.g., 43.00"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
              </div>
            </div>

            {/* OS (Left Eye) Section */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-slate-700 dark:text-slate-300 border-b border-gray-400/40 dark:border-slate-700 pb-2">
                OS (Left Eye)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryFlatKOS"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Flat K
                  </Label>
                  <Input
                    id="keratometryFlatKOS"
                    value={editValues.keratometryFlatKOS || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryFlatKOS', e.target.value)
                    }
                    placeholder="e.g., 42.50"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryFlatAxisOS"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Flat Axis
                  </Label>
                  <Input
                    id="keratometryFlatAxisOS"
                    value={editValues.keratometryFlatAxisOS || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryFlatAxisOS', e.target.value)
                    }
                    placeholder="e.g., 180"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometrySteepKOS"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Steep K
                  </Label>
                  <Input
                    id="keratometrySteepKOS"
                    value={editValues.keratometrySteepKOS || ''}
                    onChange={(e) =>
                      handleInputChange('keratometrySteepKOS', e.target.value)
                    }
                    placeholder="e.g., 43.50"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometrySteepAxisOS"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Steep Axis
                  </Label>
                  <Input
                    id="keratometrySteepAxisOS"
                    value={editValues.keratometrySteepAxisOS || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'keratometrySteepAxisOS',
                        e.target.value
                      )
                    }
                    placeholder="e.g., 90"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="keratometryAvgKOS"
                    className="text-md text-slate-600 dark:text-slate-400">
                    Avg K
                  </Label>
                  <Input
                    id="keratometryAvgKOS"
                    value={editValues.keratometryAvgKOS || ''}
                    onChange={(e) =>
                      handleInputChange('keratometryAvgKOS', e.target.value)
                    }
                    placeholder="e.g., 43.00"
                    className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KeratometrySection;
